/* eslint-disable jsx-a11y/anchor-is-valid */
import {FC, useEffect, useMemo, useState} from 'react'
import {useQuery} from 'react-query'
import {KTSVG} from '../../../_metronic/helpers'
import {PageLink, PageTitle} from '../../../_metronic/layout/core'
import {ExpansionPlantItem, ExpansionStatus} from '../../@types/expansionPortfolio'
import {getExpansionPortfolio} from '../../services/plantsService'
import {Loading} from '../components/Loadings/loading'
import {SearchInputV3} from '../components/SearchInputV3'
import {SelectGlobal} from '../components/SelectGlobal'
import {ExpansionPlantDetail} from './components/ExpansionPlantDetail'
import {ExpansionPlantList} from './components/ExpansionPlantList'
import styles from './expansionPortfolio.module.css'

const breadcrumbs: Array<PageLink> = [
  {
    title: 'Gestão Comercial',
    path: '/expansao-portfolio',
    isSeparator: false,
    isActive: false,
  },
  {
    title: '',
    path: '',
    isSeparator: true,
    isActive: false,
  },
]

const STATUS_FILTER_ITEMS = [
  {name: 'Déficit crítico', value: 'DEFICIT_CRITICO'},
  {name: 'Limitada', value: 'LIMITADA'},
  {name: 'Expansão recomendada', value: 'EXPANSAO_RECOMENDADA'},
]

const MONTHS_FILTER_ITEMS = [
  {name: '1 mês', value: '1'},
  {name: '3 meses', value: '3'},
  {name: '6 meses', value: '6'},
  {name: '12 meses', value: '12'},
  {name: '24 meses', value: '24'},
]

const TARGET_PERCENTAGE_FILTER_ITEMS = [
  {name: '80% — abaixo do break-even', value: '80'},
  {name: '100% — break-even', value: '100'},
  {name: '110% — meta padrão', value: '110'},
  {name: '120%', value: '120'},
  {name: '130%', value: '130'},
  {name: '150% — alta autossuficiência', value: '150'},
]

const ExpansionPortfolioPage: FC = () => {
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<'' | ExpansionStatus>('')
  const [monthsFilter, setMonthsFilter] = useState(6)
  const [targetPercentageFilter, setTargetPercentageFilter] = useState(110)
  const [selectedPlant, setSelectedPlant] = useState<ExpansionPlantItem | null>(null)

  const queryParams = useMemo(() => {
    const params: Record<string, string | number> = {
      months: monthsFilter,
      targetPercentage: targetPercentageFilter,
    }
    if (search) params.name = search
    if (statusFilter) params.expansionStatus = statusFilter
    return params
  }, [search, statusFilter, monthsFilter, targetPercentageFilter])

  const {data, isLoading, isFetching, isError, error} = useQuery(
    ['expansion-portfolio', queryParams],
    () => getExpansionPortfolio(queryParams),
    {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
    }
  )

  const plants: ExpansionPlantItem[] = useMemo(
    () => (data?.data ?? []).filter((plant) => plant.status !== 'SEM_DADOS'),
    [data?.data]
  )
  const months = data?.months ?? monthsFilter
  const targetPercentage = data?.targetPercentage ?? targetPercentageFilter

  useEffect(() => {
    if (!plants.length) {
      setSelectedPlant(null)
      return
    }
    setSelectedPlant((current) => {
      if (current && plants.some((plant) => plant.id === current.id)) {
        return plants.find((plant) => plant.id === current.id) || plants[0]
      }
      return plants[0]
    })
  }, [plants])

  return (
    <div>
      <div className='d-flex flex-wrap justify-content-between align-items-center gap-3 mb-5'>
        {/* <div>
          <h1 className='fs-2 fw-bolder text-gray-900 mb-1'>Expansão de Portfólio</h1>
          <div className='text-muted fs-7'>
            Identifique usinas com déficit e simule a expansão necessária
            {data?.total !== undefined ? ` · ${data.total} usina(s)` : ''}
          </div>
        </div> */}
        {isFetching && !isLoading && (
          <span className='badge badge-light-primary'>Atualizando...</span>
        )}
      </div>

      <div className={styles.layout}>
        <div className={styles.listCard}>
          <div className={styles.listHeader}>
            <div className='fs-6 fw-bold text-gray-900'>
              Usinas PREMIUM{` · ${plants.length} usina(s)`}
            </div>
            <div className='text-muted fs-8 mb-3'>Selecione uma usina para analise detalhada</div>

            <div className={styles.searchFilterRow}>
              <div className={styles.searchWrapper}>
                <SearchInputV3
                  className='form-control'
                  placeholder='Buscar usina...'
                  setMySearch={setSearch}
                />
              </div>

              <div className='dropdown'>
                <button
                  type='button'
                  className='btn btn-lg btn-light btn-active-color-primary btn-sm dropdown-toggle'
                  data-bs-toggle='dropdown'
                  aria-expanded='false'
                  data-bs-auto-close='outside'
                >
                  <span>
                    <KTSVG
                      path='/media/icons/duotune/general/gen031.svg'
                      className='svg-icon-3'
                    />
                  </span>
                </button>

                <div
                  className='dropdown-menu dropdown-menu-end p-4'
                  style={{width: 'auto', minWidth: '280px', maxWidth: '360px'}}
                >
                  {/* <label
                    className='form-label fw-bold'
                    style={{marginTop: '0.5rem', marginBottom: '0.5rem'}}
                  >
                    Filtros
                  </label> */}
                  {/* <div className='separator my-2'></div> */}

                  <div style={{marginTop: '0.5rem', marginBottom: '0.5rem', width: '100%'}}>
                    <SelectGlobal
                      itens={STATUS_FILTER_ITEMS}
                      buttonAll={true}
                      textSearch='Buscar status'
                      textButtonInit='Filtrar por STATUS'
                      labelKey='name'
                      valueKey='value'
                      typeIcon='svg'
                      linkIcon='/media/icons/duotune/general/gen019.svg'
                      sizeIconSvg='2'
                      value={statusFilter || ''}
                      className={styles.selectFilter}
                      onChange={(value: string) => {
                        setStatusFilter((value || '') as '' | ExpansionStatus)
                      }}
                    />

                    <SelectGlobal
                      itens={MONTHS_FILTER_ITEMS}
                      buttonAll={false}
                      textSearch='Buscar período'
                      textButtonInit='Filtrar por PERÍODO'
                      labelKey='name'
                      valueKey='value'
                      typeIcon='svg'
                      linkIcon='/media/icons/duotune/general/gen014.svg'
                      sizeIconSvg='2'
                      value={String(monthsFilter)}
                      className={`${styles.selectFilter} mt-1`}
                      onChange={(value: string) => {
                        setMonthsFilter(Number(value) || 6)
                      }}
                    />

                    <SelectGlobal
                      itens={TARGET_PERCENTAGE_FILTER_ITEMS}
                      buttonAll={false}
                      textSearch='Buscar meta'
                      textButtonInit='Meta de AUTOSSUFICIÊNCIA'
                      labelKey='name'
                      valueKey='value'
                      typeIcon='svg'
                      linkIcon='/media/icons/duotune/graphs/gra001.svg'
                      sizeIconSvg='2'
                      value={String(targetPercentageFilter)}
                      className={`${styles.selectFilter} mt-1`}
                      onChange={(value: string) => {
                        setTargetPercentageFilter(Number(value) || 110)
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {isLoading ? (
            <div className={styles.emptyState}>
              <Loading myClass='spinner-border text-primary' />
            </div>
          ) : isError ? (
            <div className={styles.emptyState}>
              <span className='fs-6 fw-semibold text-danger'>Erro ao carregar portfólio</span>
              <span className='fs-7 mt-1'>
                {(error as any)?.message || 'Tente novamente em instantes.'}
              </span>
            </div>
          ) : (
            <ExpansionPlantList
              plants={plants}
              selectedId={selectedPlant?.id ?? null}
              onSelect={setSelectedPlant}
            />
          )}
        </div>

        <ExpansionPlantDetail
          key={`${selectedPlant?.id || 'empty'}-${targetPercentage}`}
          plant={selectedPlant}
          months={months}
          defaultTargetPercentage={targetPercentage}
        />
      </div>
    </div>
  )
}

const ExpansionPortfolioWrapper: FC = () => {
  return (
    <>
      <PageTitle breadcrumbs={breadcrumbs}>Oportunidades de Ampliação</PageTitle>
      <ExpansionPortfolioPage />
    </>
  )
}

export {ExpansionPortfolioWrapper}
